import { useState, type FormEvent } from 'react';

import { Button } from '@/components/Button/Button';
import { Callout } from '@/components/Callout/Callout';
import { Dialog } from '@/components/Dialog/Dialog';
import { RolePicker } from '@/components/RolePicker/RolePicker';
import { TextField } from '@/components/TextField/TextField';
import type { PlatformRole } from '@/graphql/staff';
import { isStaffEmail, STAFF_DOMAIN } from '@/lib/staff';

import styles from './InviteForm.module.css';

export interface InviteFormProps {
  busy?: boolean;
  error?: string | null;
  onSubmit: (invite: { email: string; roles: PlatformRole[] }) => void;
  onCancel: () => void;
}

/** Admit a LendWell colleague to the internal portal with the roles they need. */
export function InviteForm({ busy = false, error = null, onSubmit, onCancel }: InviteFormProps) {
  const [email, setEmail] = useState('');
  const [roles, setRoles] = useState<PlatformRole[]>([]);
  const [touched, setTouched] = useState(false);

  const emailError =
    touched && !isStaffEmail(email) ? `Use a @${STAFF_DOMAIN} email address.` : undefined;
  const rolesError = touched && roles.length === 0;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (!isStaffEmail(email) || roles.length === 0) {
      return;
    }
    onSubmit({ email: email.trim().toLowerCase(), roles });
  };

  return (
    <Dialog
      actions={
        <>
          <Button disabled={busy} onClick={onCancel} variant="ghost">
            Cancel
          </Button>
          <Button form="invite-form" loading={busy} type="submit">
            Give access
          </Button>
        </>
      }
      description="They can sign in straight away with this email. No email is sent, so let them know."
      dismissable={!busy}
      onClose={onCancel}
      title="Invite someone"
    >
      <form className={styles.form} id="invite-form" noValidate onSubmit={submit}>
        <TextField
          autoComplete="off"
          data-autofocus
          disabled={busy}
          error={emailError}
          hint={`Only @${STAFF_DOMAIN} addresses can be invited.`}
          label="Work email"
          onChange={(event) => setEmail(event.target.value)}
          placeholder={`name@${STAFF_DOMAIN}`}
          type="email"
          value={email}
        />
        <RolePicker disabled={busy} onChange={setRoles} value={roles} />
        {rolesError ? (
          <p className={styles.error} role="alert">
            Choose at least one role.
          </p>
        ) : null}
        {error ? (
          <Callout tone="danger" title="That didn't work">
            {error}
          </Callout>
        ) : null}
      </form>
    </Dialog>
  );
}
